<template>
  <v-container fluid class="py-0 px-2">
    <v-text-field
      v-model="search"
      background-color="colorSearchSettings"
      clearable
      :append-icon="mdiMagnify"
      :label="$t('label.search')"
      hide-details
    />
    <div style="min-height: 1px"></div>
    <v-data-table
      :headers="headers"
      :items="tableData"
      item-key="id"
      :height="$store.state.windowSize.y - 210"
      :items-per-page="20"
      :footer-props="{
        'items-per-page-options': [10, 20, 40, 60, 80, 100],
      }"
      :search="search"
      fixed-header
      must-sort
      :loading="loaderStatus"
      class="tableCursor"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item.name }}</td>
          <td>{{ item.vatin }}</td>
          <td>{{ item.address }}</td>
          <td
            v-if="$store.state.auth.userSettings.accCompanies.update"
            class="px-2 tableSelectCursor"
            @click="((modalEdit = true), (company = { ...item }))"
          >
            <v-icon>{{ mdiPen }}</v-icon>
          </td>
          <td v-else></td>
        </tr>
      </template>
    </v-data-table>
    <v-row>
      <v-col cols="3">
        <v-btn v-if="$store.state.auth.userSettings.accCompanies.write" block color="colorNew" @click="dataNew">
          {{ $t('label.addNew') }}
        </v-btn>
      </v-col>
    </v-row>
    <!----------DIALOGS------------------------------>
    <v-dialog v-model="modalEdit" persistent width="70%">
      <v-card>
        <v-card-title class="headline colorHeadlineStandard" primary-title>
          {{ $tc('label.company') }}
          <v-spacer />
          <v-icon large @click="modalEdit = false">{{ mdiClose }}</v-icon>
        </v-card-title>
        <v-card-text class="pa-3">
          <v-form v-model="validForm" ref="companyForm">
            <v-row>
              <v-col>
                <v-text-field
                  :label="$t('label.itemName')"
                  v-model="company.name"
                  :rules="[ruleRequired]"
                  :disabled="company.id != 0"
                  maxlength="128"
                  required
                  hide-details
                  dense
                  autofocus
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field :label="$t('label.vatNumber')" maxlength="32" v-model="company.vatin" hide-details dense />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field :label="$t('label.address')" maxlength="256" v-model="company.address" hide-details dense />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-textarea
                  :label="$t('label.note')"
                  v-model="company.note"
                  name="input-7-1"
                  maxlength="512"
                  no-resize
                  hide-details
                  dense
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-btn v-if="company.id && $store.state.auth.userSettings.accCompanies.delete" color="colorDelete" @click="modalDelete = true">
            {{ $t('btn.delete') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="colorSave" @click="dataSave">{{ $t('btn.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DialogConfirmDelete
      :header="$t('label.delete')"
      :body="`${$t('msg.confirmDelete')}: ${company.name}?`"
      :openDialog.sync="modalDelete"
      v-on:confirm="dataDelete"
    />
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue';
import axios from 'axios';
import DialogConfirmDelete from '@/components/DialogConfirmDelete.vue';

import { mdiMagnify, mdiPen, mdiClose } from '@mdi/js';

export default Vue.extend({
  name: 'SettingsCompanies',
  components: { DialogConfirmDelete },
  data: () => ({
    tableData: [],
    loaderStatus: false,
    modalEdit: false,
    modalDelete: false,
    validForm: false,
    search: '',
    company: {
      id: 0,
      name: '',
      vatin: '',
      note: '',
      address: '',
    },
    mdiMagnify: mdiMagnify,
    mdiPen: mdiPen,
    mdiClose: mdiClose,
  }),
  computed: {
    ruleRequired() {
      return (v: any) => !!v || this.$i18n.t('msg.fieldIsMandatory');
    },
    headers: function () {
      return [
        { text: this.$i18n.t('label.itemName'), value: 'name' },
        { text: this.$i18n.t('label.vatNumber'), value: 'vatin' },
        { text: this.$i18n.t('label.address'), value: 'address' },
        { sortable: false, width: '48px' },
      ];
    },
  },
  mounted() {
    this.dataLoad();
  },
  methods: {
    dataLoad() {
      this.loaderStatus = true;
      axios
        .get(`/api/v1/company`)
        .then((response) => {
          this.tableData = response.data;
          this.loaderStatus = false;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
          this.loaderStatus = false;
        });
    },
    dataNew() {
      const defaults: any = this.$options;
      this.company = { ...defaults.data().company };
      this.modalEdit = true;
      this.$nextTick(function () {
        const form: any = this.$refs.companyForm;
        form.validate();
      });
    },
    dataSave() {
      const form: any = this.$refs.companyForm;
      if (form.validate() !== true) {
        this.$store.commit('globalMessage', this.$i18n.t('msg.formNotValid'));
      } else {
        if (this.company.id === 0) {
          axios
            .post('/api/v1/company', this.company)
            .then((response) => {
              if (response.data.newCompanyID === -1) {
                this.$store.commit('globalMessage', this.$i18n.t('msg.itemNameExists'));
              } else {
                this.dataLoad();
                this.modalEdit = false;
              }
            })
            .catch((err) => {
              this.$store.dispatch('catchErrHandler', err);
            });
        } else {
          axios
            .put('/api/v1/company', this.company)
            .then(() => {
              this.dataLoad();
              this.modalEdit = false;
            })
            .catch((err) => {
              this.$store.dispatch('catchErrHandler', err);
            });
        }
      }
    },
    dataDelete() {
      axios
        .delete(`/api/v1/company/${this.company.id}`)
        .then(() => {
          this.dataLoad();
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
      this.modalDelete = false;
      this.modalEdit = false;
    },
  },
});
</script>
